import { useCallback, useState } from 'react'
import ApiRoute from '../../../components/ApiRoute'

const PreferenceField = ({ label, field, value, onChange, onPatch, patchDisabled, isApiCalling, type, options }) => {
  return (
    <div className='grid grid-cols-[160px_1fr_auto] gap-2 items-center py-1'>
      <label htmlFor={`pref_${field}`} className='text-sm font-bold text-black text-right pr-2'>
        {label}
      </label>
      {type === 'select'
        ? (
          <select
            id={`pref_${field}`}
            name={field}
            onChange={(e) => onChange(field, e.target.value)}
            value={value || ''}
            className='border border-gray-100 text-gray-700 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2'
          >
            <option value=''>Select...</option>
            {options.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          )
        : (
          <input
            type='text'
            id={`pref_${field}`}
            name={field}
            onChange={(e) => onChange(field, e.target.value)}
            value={value || ''}
            className='border border-gray-100 text-gray-700 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2'
          />
          )}
      <ApiRoute method='PATCH' route='/contact/{contactId}/preferences' onClick={() => onPatch(field)} disabled={isApiCalling || patchDisabled} />
    </div>
  )
}

const FIELDS = [
  {
    label: 'Plan Category',
    field: 'planCategory',
    type: 'select',
    options: [
      { value: 'MAPD', label: 'MAPD' },
      { value: 'PDP', label: 'PDP' },
      { value: 'MEDSUPP', label: 'MEDSUPP' }
    ]
  },
  {
    label: 'Include SNP Plans',
    field: 'includeSNPPlans',
    type: 'select',
    dataType: 'boolean',
    options: [
      { value: 'true', label: 'true' },
      { value: 'false', label: 'false' }
    ]
  },
  {
    label: 'Include MSA Plans',
    field: 'includeMSAPlans',
    type: 'select',
    dataType: 'boolean',
    options: [
      { value: 'true', label: 'true' },
      { value: 'false', label: 'false' }
    ]
  }
]

const Preferences = ({ activeSession, callApiFn, isApiCalling }) => {
  const [values, setValues] = useState(activeSession?.preferences || {})

  const updateField = useCallback((field, value) => {
    setValues({
      ...values,
      [field]: value
    })
  }, [values])

  const coerceValue = (field, val) => {
    const fieldDef = FIELDS.find(f => f.field === field)
    if (fieldDef?.dataType === 'boolean') return val === 'true'
    return val
  }

  const patchPreferences = useCallback(async (field) => {
    await callApiFn({
      method: 'PATCH',
      route: 'contact/{id}/preferences',
      body: {
        [field]: coerceValue(field, values[field])
      },
      saveSuccessPath: 'preferences'
    })
  }, [values, callApiFn])

  const putPreferences = useCallback(async () => {
    const body = {}
    for (const [key, val] of Object.entries(values)) {
      if (val !== '' && val !== undefined) {
        body[key] = coerceValue(key, val)
      }
    }
    await callApiFn({
      method: 'PUT',
      route: 'contact/{id}/preferences',
      body,
      saveSuccessPath: 'preferences'
    })
  }, [values, callApiFn])

  const getPreferences = () => {
    callApiFn({
      method: 'GET',
      route: 'contact/{id}/preferences',
      saveSuccessPath: 'preferences'
    })
  }

  const patchDisabled = !activeSession?.contact

  return (
    <div>
      {!activeSession?.contact && (
        <div className='bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded my-2'>
          No contact selected. Create a contact first.
        </div>
      )}

      <div className='mb-3'>
        {FIELDS.map(({ label, field, type, options }) => (
          <PreferenceField
            key={field}
            label={label}
            field={field}
            type={type}
            options={options}
            value={values[field]}
            onChange={updateField}
            onPatch={patchPreferences}
            patchDisabled={patchDisabled}
            isApiCalling={isApiCalling}
          />
        ))}
      </div>

      <div className='flex gap-2 flex-wrap'>
        <ApiRoute method='PUT' route='/contact/{contactId}/preferences' onClick={putPreferences} disabled={isApiCalling || patchDisabled} />
        <ApiRoute method='GET' route='/contact/{contactId}/preferences' onClick={getPreferences} disabled={isApiCalling || patchDisabled} />
      </div>
    </div>
  )
}

export default Preferences
